/* eslint-disable max-lines-per-function */
import {
  FieldOwnerType as FieldOwnerTypeEnum,
  FieldSetType,
} from "_graphql-types-frontend/graphql";
import { Popconfirm } from "../../Overlay/Overlay";
import { Tree } from "../../Tree/Tree";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../Modal";
import {
  TemplateParamsContext,
  useGetMetadata,
  useTemplate,
  useTemplateStoreFromContext,
} from "../Context/hooks";
import FieldTypeahead from "../Field/FieldTypeahead";
import { FieldOwnerType } from "../types";
import {
  draftImportedFieldsOnToInvestment,
  fieldsDataIsEmpty,
  renderImportTree,
} from "./helpers";

export function AvailableFieldsToImport({
  onChange,
}: {
  onChange: (fieldKeys: string[]) => void;
}): JSX.Element | null {
  const sourceTemplateStore = useTemplateStoreFromContext();

  // Use individual selectors to avoid creating new objects
  const fieldsData = sourceTemplateStore(state => state.publishedData);
  const content = sourceTemplateStore(state => state.content);
  const loading = sourceTemplateStore(state => state.loading);

  const getMetadata = useGetMetadata();

  if (fieldsData == null) return null;
  if (fieldsDataIsEmpty(fieldsData)) return null;

  if (loading) {
    return <i className="fa fa-spinner fa-spin" />;
  }

  const treeData = renderImportTree({
    content,
    path: "",
    importingFieldsData: fieldsData,
    getMetadata,
  });

  return (
    <div className="import-fields-modal__content">
      <h4>Select Fields to Import</h4>
      <Tree
        checkable
        onCheck={checked => {
          onChange(checked as string[]);
        }}
        treeData={treeData}
      />
    </div>
  );
}

function ImportFieldsModal({
  visible,
  onCancel,
  templateName,
  ownerType,
}: {
  visible: boolean;
  onCancel: () => void;
  templateName: FieldSetType;
  ownerType: FieldOwnerType;
}): JSX.Element {
  const [keysToImport, setKeysToImport] = useState<string[]>([]);
  const [sourceEntity, setSourceEntity] = useState<{
    id: string | number;
    name: string;
  }>({ id: "", name: "" });

  const { investmentId } = useParams<{ investmentId: string }>();

  const targetTemplateStore = useTemplate({
    ownerId: Number(investmentId),
    name: templateName,
    ownerType,
    editMode: false,
  });

  const { saveFields: saveTargetFields } = targetTemplateStore(state => state);

  const sourceContextParams = useMemo(
    () => ({
      ownerId: sourceEntity.id
        ? typeof sourceEntity.id === "number"
          ? sourceEntity.id
          : Number(sourceEntity.id) || null
        : null,
      name: templateName,
      ownerType,
      editMode: false, // Import fields always uses non-edit mode
    }),
    [sourceEntity.id, templateName, ownerType]
  );

  const sourceTemplateStore = useTemplate(sourceContextParams);

  // Use individual selectors to avoid creating new objects
  const sourcePublishedData = sourceTemplateStore(state => state.publishedData);

  // Determine if we should use the data based on validation
  const hasValidSourceData = useMemo(() => {
    const hasValidId =
      sourceEntity.id &&
      (typeof sourceEntity.id === "number"
        ? sourceEntity.id > 0
        : String(sourceEntity.id).trim() !== "");

    return (
      hasValidId && sourceContextParams.ownerId !== null && sourcePublishedData
    );
  }, [sourceEntity.id, sourceContextParams.ownerId, sourcePublishedData]);

  const onChange = useCallback(
    (fieldKeys: string[]) => {
      setKeysToImport(fieldKeys);
    },
    [setKeysToImport]
  );

  const CONFIMATION_MESSAGE =
    sourceEntity &&
    `Are you sure you want to replace the selected fields with data from ${sourceEntity?.name}`;

  return (
    <Modal
      title={"Import Fields"}
      visible={visible}
      onCancel={onCancel}
      zIndex={999}
      footer={
        <div className="main-modal__footer">
          <Popconfirm
            title={CONFIMATION_MESSAGE}
            onConfirm={_e =>
              draftImportedFieldsOnToInvestment({
                keysToImport,
                targetEntityId: Number(investmentId),
                publishedSourceData: sourcePublishedData,
                saveTargetFields: saveTargetFields,
                closeModal: onCancel,
              })
            }
            okText="Yes"
            cancelText="No"
            disabled={!hasValidSourceData || keysToImport.length === 0}
          >
            <button
              type="button"
              className="rounded-btn rounded-btn--blue mr-16"
              disabled={!hasValidSourceData || keysToImport.length === 0}
            >
              Import as Drafts
            </button>
          </Popconfirm>
        </div>
      }
    >
      <div data-cy="import-fields-modal">
        <label htmlFor="fieldtypeahead" className="main-input__label">
          {ownerType === FieldOwnerTypeEnum.Firm
            ? "Select Firm to Import From:"
            : "Select Investment to Import From:"}
        </label>

        <FieldTypeahead
          placeholder={"Name"}
          dropdownStyle={{ maxWidth: "100%" }}
          dropdownMatchSelectWidth={false}
          onSelect={({ id, name }) => {
            setSourceEntity({ id, name });
          }}
          ownerType={ownerType}
          dropdownClassName="tm-empty-card__add-member-menu"
        />
        <TemplateParamsContext.Provider value={sourceContextParams}>
          {hasValidSourceData ? (
            <AvailableFieldsToImport onChange={onChange} />
          ) : null}
        </TemplateParamsContext.Provider>
      </div>
    </Modal>
  );
}

export default ImportFieldsModal;
